import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Check, X } from "lucide-react";

const checkRules = (p) => ({
  length: p.length >= 8,
  upper: /[A-Z]/.test(p),
  number: /\d/.test(p),
  special: /[^A-Za-z0-9]/.test(p),
});

export default function ChangePassword() {
  const { user, changePassword, logout } = useAuth();
  const navigate = useNavigate();
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!user) return <Navigate to="/login" replace />;

  const rules = checkRules(newPwd);
  const allOk = Object.values(rules).every(Boolean) && newPwd === confirm;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) {
      setError("Please meet all password requirements and ensure passwords match.");
      return;
    }
    setSubmitting(true);
    const res = await changePassword(oldPwd, newPwd);
    setSubmitting(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Password updated");
    navigate("/");
  };

  const Rule = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-xs">
      {ok ? <Check className="h-3.5 w-3.5 text-green-600" /> : <X className="h-3.5 w-3.5 text-slate-400" />}
      <span className={ok ? "text-green-700" : "text-slate-500"}>{label}</span>
    </div>
  );

  return (
    <div
      data-testid="change-password-page"
      className="min-h-screen flex items-center justify-center bg-slate-50 p-4"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm p-7">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck className="h-5 w-5 brand-text" />
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Set a new password
          </h1>
        </div>
        <p className="text-sm text-slate-500 mb-6">
          For your security, please change your password before continuing.
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <Label htmlFor="old">Current password</Label>
            <Input
              id="old"
              data-testid="cp-old-input"
              type="password"
              value={oldPwd}
              required
              onChange={(e) => setOldPwd(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              data-testid="cp-new-input"
              type="password"
              value={newPwd}
              required
              onChange={(e) => setNewPwd(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input
              id="confirm"
              data-testid="cp-confirm-input"
              type="password"
              value={confirm}
              required
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-md p-3">
            <Rule ok={rules.length} label="At least 8 characters" />
            <Rule ok={rules.upper} label="Uppercase letter" />
            <Rule ok={rules.number} label="Number" />
            <Rule ok={rules.special} label="Special character" />
            <Rule ok={newPwd && newPwd === confirm} label="Passwords match" />
          </div>

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          <Button
            data-testid="cp-submit-btn"
            disabled={submitting || !allOk}
            type="submit"
            className="w-full brand-bg brand-bg-hover text-white"
          >
            {submitting ? "Updating..." : "Update password"}
          </Button>
          <button
            type="button"
            data-testid="cp-cancel-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full text-xs text-slate-500 hover:text-slate-700 mt-1"
          >
            Sign out instead
          </button>
        </form>
      </div>
    </div>
  );
}
